import React, { useState, useRef, useEffect, useContext } from "react";

import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import { SocketContext } from "../../context/Socket/SocketContext";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import ChatIcon from "@material-ui/icons/Chat";

import TicketListItem from "../TicketListItemCustom";
import useTickets from "../../hooks/useTickets";
import useNotificationSound from "../../hooks/useNotificationSound";
import alertSound from "../../assets/sound.mp3";
import pendingSound from "../../assets/phone-alert-marimba-bubble-om-fx-1-00-01.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import { useMutedContacts } from "../../context/MutedContacts/MutedContactsContext";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import tabAlert from "../../helpers/tabAlert";
import { isTicketInAlertScope, shouldAlertForTicket } from "../../helpers/canAccessTicket";

// A mesma mensagem chega várias vezes ao mesmo navegador: o backend emite uma
// vez por sala (fila, notificação, contato, ticket), a Cloud API reemite pelo
// webhook e a reconexão reenvia o cache de eventos. Sem isto, tocava 2-3x.
const NOTIFIED_IDS_LIMIT = 300;

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 350,
		...theme.scrollbarStyles,
	},
	popoverPaper: {
		width: "100%",
		maxWidth: 350,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			maxWidth: 270,
		},
	},
	noShadow: {
		boxShadow: "none !important",
	},
}));

const NotificationsPopOver = ({ volume }) => {
	const classes = useStyles();

	const history = useHistory();
	const { user } = useContext(AuthContext);
	const ticketIdUrl = +history.location.pathname.split("/")[2];
	const ticketIdRef = useRef(ticketIdUrl);
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);

	const [showPendingTickets, setShowPendingTickets] = useState(false);

	const [, setDesktopNotifications] = useState([]);

	const { tickets } = useTickets({ withUnreadMessages: "true", enabled: isOpen });

	const play = useNotificationSound(alertSound, volume);
	const playPending = useNotificationSound(pendingSound, volume);
	const soundAlertRef = useRef();
	const pendingSoundRef = useRef();
	const notifiedIdsRef = useRef(new Set());

	const historyRef = useRef(history);

  const socketManager = useContext(SocketContext);
	const { connectionScopeEnabled, notifyGroupMessages } = useContext(SettingsContext);
	const { isContactMutedRef } = useMutedContacts();

	// O backend grava "enabled"; versões antigas do front gravavam "enable". Aceitar ambos.
	const seesAllTickets =
		user?.allTicket === "enabled" ||
		user?.allTicket === "enable" ||
		user?.profile === "admin";

	useEffect(() => {
		const fetchSettings = async () => {
			try {
				if (seesAllTickets) {
					setShowPendingTickets(true);
				}
			} catch (err) {
			  	toastError(err);
			}
		}

		fetchSettings();
	}, [seesAllTickets]);

	useEffect(() => {
		soundAlertRef.current = play;
		pendingSoundRef.current = playPending;
	}, [play, playPending]);

	useEffect(() => {
		const processNotifications = () => {
			if (showPendingTickets) {
				setNotifications(tickets);
			} else {
				const newNotifications = tickets.filter(ticket => ticket.status !== "pending");

				setNotifications(newNotifications);
			}
		}

		processNotifications();
	}, [tickets]);

	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	useEffect(() => {
    const socket = socketManager.getSocket(user.companyId);

		socket.on("ready", () => socket.emit("joinNotification"));

		// Chamado com atendente: só o computador do atendente. Aguardando: quem pode
		// abrir o chamado (mesmo helper da lista, espelho do backend). Antes, admin e
		// allTicket pulavam essa checagem e ouviam o Aguardando de toda fila/conexão.
		// A mesma regra decide quem ouve a ligação recebida (CallContext). Grupo só
		// entra com a setting `notifyGroupMessages` da empresa.
		const isInUserScope = ticket =>
			isTicketInAlertScope(user, ticket, connectionScopeEnabled, notifyGroupMessages);

		// Admin abre qualquer chamado, mas o som do Aguardando é só das filas em que
		// ele está cadastrado (sem fila continua tocando). O badge não muda.
		const shouldPlaySound = ticket =>
			shouldAlertForTicket(user, ticket, connectionScopeEnabled, notifyGroupMessages);

		const isAlreadyNotified = messageId => {
			if (!messageId) return false;
			const notified = notifiedIdsRef.current;
			if (notified.has(messageId)) return true;

			notified.add(messageId);
			if (notified.size > NOTIFIED_IDS_LIMIT) {
				notified.delete(notified.values().next().value);
			}
			return false;
		};

		socket.on(`company-${user.companyId}-ticket`, data => {
			if (data.action === "updateUnread" || data.action === "delete") {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticketId);
					if (ticketIndex !== -1) {
						prevState.splice(ticketIndex, 1);
						return [...prevState];
					}
					return prevState;
				});

				setDesktopNotifications(prevState => {
					const notfiticationIndex = prevState.findIndex(
						n => n.tag === String(data.ticketId)
					);
					if (notfiticationIndex !== -1) {
						prevState[notfiticationIndex].close();
						prevState.splice(notfiticationIndex, 1);
						return [...prevState];
					}
					return prevState;
				});
			}

			// Chamado que caiu no "Em atendimento" dele pela mão de outro (IA,
			// colega, automação): mesmo som do Aguardando. O backend só manda
			// `ownerAlertId` nesse caso; quem assume para si não recebe.
			if (data.action === "update" && data.ownerAlertId) {
				const { ticket } = data;
				if (!ticket || !isInUserScope(ticket) || !ticket.userId) return;
				if (ticket.contactId && isContactMutedRef?.current?.(ticket.contactId)) return;
				if (ticket.id === ticketIdRef.current && document.visibilityState === "visible") return;
				if (isAlreadyNotified(data.ownerAlertId)) return;

				if (pendingSoundRef.current) pendingSoundRef.current();
				if (!document.hasFocus()) tabAlert.bump();
			}
		});

		socket.on(`company-${user.companyId}-appMessage`, data => {
			if (data.action !== "create" || data.message.fromMe) return;

			const { ticket } = data;
			if (!isInUserScope(ticket)) return;

			const isPending = ticket.status === "pending";

			if (!isPending || showPendingTickets) {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === ticket.id);
					if (ticketIndex !== -1) {
						prevState[ticketIndex] = ticket;
						return [...prevState];
					}
					return [ticket, ...prevState];
				});
			}

			// Contato silenciado pelo usuário: o ticket continua entrando na lista e no
			// badge acima, mas não faz barulho nem abre popup do sistema. Em grupo o
			// contato do ticket é o próprio grupo, então isso não cala o privado dos
			// integrantes.
			const contactId = ticket?.contactId ?? data.contact?.id;
			if (contactId && isContactMutedRef?.current?.(contactId)) return;

			// Quem já está lendo esse ticket com a aba à vista não precisa de alerta.
			const isOpenAndVisible =
				data.message.ticketId === ticketIdRef.current &&
				document.visibilityState === "visible";

			if (isOpenAndVisible) return;

			if (!shouldPlaySound(ticket)) return;
			if (isAlreadyNotified(data.message.id)) return;

			// O som vem primeiro: se o popup falhar (permissão negada, contexto
			// inseguro), o alerta sonoro não pode ir junto.
			const playAlert = isPending ? pendingSoundRef.current : soundAlertRef.current;
			if (playAlert) playAlert();

			if (!document.hasFocus()) {
				showDesktopNotification(data);
				tabAlert.bump();
			}
		});

		return () => {
			// ✅ ManagedSocket.disconnect() remove APENAS os listeners e joins DESTE socket gerenciado
			socket.disconnect();
		};
	}, [user, showPendingTickets, socketManager, connectionScopeEnabled, notifyGroupMessages, isContactMutedRef]);

	const showDesktopNotification = data => {
		const { message, contact, ticket } = data;

		if (!("Notification" in window) || Notification.permission !== "granted") return;

		const options = {
			body: `${message.body} - ${format(new Date(), "HH:mm")}`,
			icon: contact.urlPicture,
			tag: String(ticket.id),
			renotify: true,
		};

		let notification;
		try {
			notification = new Notification(
				`${i18n.t("tickets.notification.message")} ${contact.name}`,
				options
			);
		} catch (err) {
			// Chrome no Android lança ao construir Notification fora de um Service Worker.
			// O som e o alerta na aba já cobriram o usuário; não há o que fazer aqui.
			return;
		}

		notification.onclick = e => {
			e.preventDefault();
			window.focus();
			historyRef.current.push(`/tickets/${ticket.uuid}`);
		};

		setDesktopNotifications(prevState => {
			const notfiticationIndex = prevState.findIndex(
				n => n.tag === notification.tag
			);
			if (notfiticationIndex !== -1) {
				prevState[notfiticationIndex] = notification;
				return [...prevState];
			}
			return [notification, ...prevState];
		});
	};

	const handleClick = () => {
		setIsOpen(prevState => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	const NotificationTicket = ({ children }) => {
		return <div onClick={handleClickAway}>{children}</div>;
	};

	return (
		<>
			<Tooltip title={i18n.t("mainDrawer.appBar.tooltips.tickets")}>
				<IconButton
					onClick={handleClick}
					ref={anchorEl}
					aria-label={i18n.t("mainDrawer.appBar.tooltips.tickets")}
					color="inherit"
					style={{color:"white"}}
				>
					<Badge overlap="rectangular" badgeContent={notifications.length} color="secondary">
						<ChatIcon />
					</Badge>
				</IconButton>
			</Tooltip>
			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
			>
				<List dense className={classes.tabContainer}>
					{notifications.length === 0 ? (
						<ListItem>
							<ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
						</ListItem>
					) : (
						notifications.map(ticket => (
							<NotificationTicket key={ticket.id}>
								<TicketListItem ticket={ticket} />
							</NotificationTicket>
						))
					)}
				</List>
			</Popover>
		</>
	);
};

export default NotificationsPopOver;